/**
 * First-party, privacy-preserving analytics tracker — v3.
 * UK DUAA 2025 "statistical purposes" design:
 * - No cookies, no IP storage, no fingerprinting, no cross-site tracking
 * - Per-tab session id (sessionStorage) + anonymous first-party visitor id
 *   (random hex in localStorage, ~13-month expiry) for unique-visitor counts
 * - Engagement proof: engaged time, scroll depth and interaction signals are
 *   reported so the server can separate real humans from JS-executing bots
 * - Objection honoured: "Essential only" in the cookie banner stops everything
 * - Silently fails on network errors so it never breaks UX
 */
import axios from 'axios';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VID_TTL_MS = 34186698000; // ~13 months

function objected() {
  try {
    return localStorage.getItem('gl_cookie_consent') === 'rejected';
  } catch (_e) {
    return false;
  }
}

function randHex(bytes) {
  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (b) => b.toString(16).padStart(2, '0')).join('');
}

function sessionId() {
  try {
    let sid = sessionStorage.getItem('gl_sid');
    if (!sid) {
      sid = randHex(12);
      sessionStorage.setItem('gl_sid', sid);
    }
    return sid;
  } catch (_e) {
    return null;
  }
}

function visitorId() {
  try {
    const raw = localStorage.getItem('gl_vid');
    if (raw) {
      const o = JSON.parse(raw);
      if (o.id && Date.now() - (o.t || 0) < VID_TTL_MS) return o.id;
    }
    const id = randHex(16);
    localStorage.setItem('gl_vid', JSON.stringify({ id, t: Date.now() }));
    try { sessionStorage.setItem('gl_vid_new', '1'); } catch (_e) { /* noop */ }
    return id;
  } catch (_e) {
    return null;
  }
}

function isNewVisitor() {
  try { return sessionStorage.getItem('gl_vid_new') === '1'; } catch (_e) { return false; }
}

function utmParams() {
  try {
    const q = new URLSearchParams(window.location.search);
    const out = {};
    ['source', 'medium', 'campaign'].forEach((k) => {
      const v = q.get(`utm_${k}`);
      if (v) out[k] = v.slice(0, 80);
    });
    return Object.keys(out).length ? out : null;
  } catch (_e) {
    return null;
  }
}

// v4: landing attribution (referrer + UTM) is captured once per session and
// re-sent with every page view, so channel/source survive a lost landing packet.
function sessionAttr() {
  try {
    const raw = sessionStorage.getItem('gl_attr');
    if (raw) return JSON.parse(raw);
    const attr = { ref: (document.referrer || '').slice(0, 500) };
    const utm = utmParams();
    if (utm) attr.utm = utm;
    sessionStorage.setItem('gl_attr', JSON.stringify(attr));
    return attr;
  } catch (_e) {
    return null;
  }
}

async function send(type, { path, meta, extra } = {}) {
  if (objected()) return;
  try {
    await axios.post(`${API_BASE}/track`, {
      type,
      path: path ?? window.location.pathname,
      meta: meta || {},
      sid: sessionId(),
      vid: visitorId(),
      wd: navigator.webdriver === true,
      ...(extra || {}),
    }, { timeout: 4000 });
  } catch (_e) {
    /* swallow — tracker must never break the page */
  }
}

// ---- Engagement measurement (per-page engaged time + scroll depth) ----
const pg = { path: null, engagedMs: 0, lastTick: 0, lastActivity: 0, maxScroll: 0, interacted: false };
let engageBound = false;
let lastPv = { key: null, t: 0 };

function tickEngaged() {
  const now = Date.now();
  if (pg.path && document.visibilityState === 'visible' && now - pg.lastActivity < 30000) {
    pg.engagedMs += Math.min(now - pg.lastTick, 5500);
  }
  pg.lastTick = now;
}

function onActivity() {
  pg.lastActivity = Date.now();
  pg.interacted = true;
}

function onScroll() {
  onActivity();
  try {
    const h = document.documentElement.scrollHeight - window.innerHeight;
    const pct = h > 0 ? Math.min(100, Math.round(((window.scrollY || 0) / h) * 100)) : 100;
    if (pct > pg.maxScroll) pg.maxScroll = pct;
  } catch (_e) { /* noop */ }
}

function flushEngage(useBeacon) {
  if (!pg.path || objected()) return;
  tickEngaged();
  const ms = Math.round(pg.engagedMs);
  if (ms < 1000 && !pg.interacted && pg.maxScroll < 25) { pg.engagedMs = 0; return; }
  const payload = {
    type: 'engage',
    path: pg.path,
    meta: {},
    sid: sessionId(),
    vid: visitorId(),
    wd: navigator.webdriver === true,
    engaged_ms: Math.min(ms, 1800000),
    scroll_pct: pg.maxScroll,
    interacted: pg.interacted,
  };
  pg.engagedMs = 0;
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(`${API_BASE}/track`, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      return;
    }
  } catch (_e) { /* fall through to axios */ }
  axios.post(`${API_BASE}/track`, payload, { timeout: 4000 }).catch(() => {});
}

function beginPage(path) {
  flushEngage(false);
  const now = Date.now();
  pg.path = path;
  pg.lastTick = now;
  pg.lastActivity = now;
  pg.maxScroll = 0;
  pg.interacted = false;
  if (!engageBound) {
    engageBound = true;
    ['pointermove', 'pointerdown', 'keydown', 'touchstart'].forEach((ev) =>
      document.addEventListener(ev, onActivity, { passive: true, capture: true }));
    window.addEventListener('scroll', onScroll, { passive: true });
    setInterval(tickEngaged, 5000);
    window.addEventListener('pagehide', () => flushEngage(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushEngage(true);
    });
  }
}

function firePageView(fullPath, pathname) {
  // Uppercase variants are redirected to the canonical lowercase URL, which
  // fires its own page view — skipping here prevents a duplicate.
  if (/[A-Z]/.test(pathname)) return;
  // Dedup double-fires (StrictMode remounts, rapid redirects)
  const now = Date.now();
  if (lastPv.key === fullPath && now - lastPv.t < 1500) return;
  lastPv = { key: fullPath, t: now };
  const extra = {};
  const attr = sessionAttr();
  if (attr) extra.attr = attr;
  try {
    if (!sessionStorage.getItem('gl_landed')) {
      sessionStorage.setItem('gl_landed', '1');
      extra.landing = true;
      extra.new_visitor = isNewVisitor();
    }
  } catch (_e) { /* noop */ }
  beginPage(pathname);
  send('page_view', { path: fullPath, extra });
}

export const track = {
  pageView: (path) => firePageView(path, path.split('?')[0]),
  newsletterSignup: (source) => send('newsletter_signup', { meta: { source } }),
  itineraryBuild: (itemCount) => send('itinerary_build', { meta: { item_count: itemCount } }),
  externalLink: (target, slug, action) => send('external_link', { meta: { target, slug, action: action || 'website' } }),
  search: (query, filters) => send('search', { meta: { query, filters } }),
  awardVote: (categoryId) => send('award_vote', { meta: { category_id: categoryId } }),
  awardNomination: (categoryId) => send('award_nomination', { meta: { category_id: categoryId } }),
  attractionView: (slug) => send('attraction_view', { meta: { slug } }),
};

const BOOK_RX = /ticket|book|eventbrite|skiddle|digitickets|seetickets|ticketsource|designmynight|opentable|resdiary|checkfront|beyonk/i;

// Classify what the visitor was trying to do: website / book / directions / phone / email.
function outboundAction(a, url) {
  const explicit = a.getAttribute('data-outbound');
  if (explicit) return explicit;
  if (/maps\.google|google\.[^/]+\/maps|maps\.apple/i.test(url.href)) return 'directions';
  if (BOOK_RX.test(url.href)) return 'book';
  return 'website';
}

/**
 * Hook this once near the root — fires a page_view on every route change and
 * tracks EVERY outbound external-link click sitewide via a delegated listener.
 * Skips admin paths to avoid polluting traffic data with our own activity.
 */
export function useRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    firePageView(location.pathname + location.search, location.pathname);
  }, [location.pathname, location.search]);

  useEffect(() => {
    function onClick(e) {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (window.location.pathname.startsWith('/admin')) return;
      const href = a.getAttribute('href') || '';
      if (/^tel:/i.test(href)) { track.externalLink('phone', window.location.pathname, 'phone'); return; }
      if (/^mailto:/i.test(href)) { track.externalLink('email', window.location.pathname, 'email'); return; }
      if (!/^https?:\/\//i.test(href)) return;
      try {
        const url = new URL(href);
        if (url.host === window.location.host) return;
        track.externalLink(url.hostname.replace(/^www\./, ''), window.location.pathname, outboundAction(a, url));
      } catch (_e) { /* malformed href — ignore */ }
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}
